/**
 * OpenELP Personalization Engine
 *
 * The brain behind truly personalized learning:
 * - Spaced Repetition System (SM-2 adapted for kids)
 * - Weakness Profile (which skills need work)
 * - Error Pattern Analysis (common mistake types)
 * - Adaptive Difficulty Engine (real-time curve)
 * - Learning Style Adaptation (imagination/visual/expression/challenge)
 * - Smart Review Recommendations (what to study next)
 * - Mastery Tracking (word/sentence/skill mastery levels)
 * - Metacognitive Insights (self-awareness prompts)
 */

import type { LearningLayer, LearningStyleType } from '@/types'

// ========================
// Spaced Repetition (SM-2 Adapted)
// ========================

export interface SRSCard {
  word: string
  /** Ease factor — how easy the student finds this word (1.3–2.8) */
  ease: number
  /** Interval in days until next review */
  interval: number
  /** Times reviewed */
  repetitions: number
  /** Last review timestamp */
  lastReview: number
  /** Next review timestamp */
  nextReview: number
  /** Mastery level 0-100 */
  mastery: number
  /** History of recent attempts (true=correct) */
  history: boolean[]
}

export function createSRSCard(word: string): SRSCard {
  const now = Date.now()
  return {
    word,
    ease: 2.3,
    interval: 0,
    repetitions: 0,
    lastReview: now,
    nextReview: now, // Available immediately
    mastery: 0,
    history: [],
  }
}

/**
 * Update an SRS card after a review attempt.
 * Adapted SM-2 algorithm with kid-friendly parameters.
 */
export function reviewSRSCard(card: SRSCard, correct: boolean, responseTimeMs?: number): SRSCard {
  const now = Date.now()
  const newHistory = [...card.history, correct].slice(-10) // Keep last 10

  // Factor in response time — fast correct answers boost mastery faster
  let quality = correct ? 4 : 1 // 0-5 scale, adapted
  if (correct && responseTimeMs) {
    if (responseTimeMs < 3000) quality = 5      // Very fast = perfect
    else if (responseTimeMs < 6000) quality = 4  // Good speed
    else quality = 3                              // Slow but correct
  }

  let { ease, interval, repetitions, mastery } = card

  if (correct) {
    repetitions += 1
    // Mastery climbs with correct answers, faster at first (beginner's bonus)
    const masteryGain = quality === 5 ? 22 : quality === 4 ? 16 : 10
    mastery = Math.min(100, mastery + masteryGain)
  } else {
    repetitions = 0
    // Wrong answers drop mastery but not to zero — encourage retry
    mastery = Math.max(0, mastery - 15)
  }

  // SM-2 ease update
  ease = ease + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02))
  ease = Math.max(1.3, Math.min(2.8, ease))

  // Interval calculation
  if (!correct) {
    interval = 0 // Review again soon
  } else if (repetitions === 1) {
    interval = 1
  } else if (repetitions === 2) {
    interval = 3
  } else {
    interval = Math.round(interval * ease)
  }

  return {
    ...card,
    ease,
    interval,
    repetitions,
    lastReview: now,
    nextReview: now + interval * 24 * 60 * 60 * 1000,
    mastery,
    history: newHistory,
  }
}

export function isDueForReview(card: SRSCard): boolean {
  return Date.now() >= card.nextReview
}

export function getMasteryLevel(mastery: number): 'novice' | 'apprentice' | 'adept' | 'master' {
  if (mastery >= 85) return 'master'
  if (mastery >= 60) return 'adept'
  if (mastery >= 30) return 'apprentice'
  return 'novice'
}

export function getMasteryEmoji(level: ReturnType<typeof getMasteryLevel>): string {
  switch (level) {
    case 'master': return '👑'
    case 'adept': return '⭐'
    case 'apprentice': return '🌱'
    case 'novice': return '🔹'
  }
}

// ========================
// Weakness Profile
// ========================

export type SkillCategory = 'vocab' | 'spelling' | 'sentence' | 'listening' | 'speaking' | 'phonics' | 'grammar'

export interface SkillStat {
  category: SkillCategory
  attempts: number
  correct: number
  /** Recent accuracy (last 10 attempts) */
  recentAccuracy: number
  /** Trend: improving, declining, or stable */
  trend: 'improving' | 'declining' | 'stable'
  /** Average response time in ms */
  avgResponseTime: number
  lastUpdated: number
}

export interface WeaknessProfile {
  skills: Record<SkillCategory, SkillStat>
  /** Categories sorted by weakness (lowest accuracy first) */
  weakestAreas: SkillCategory[]
  /** Categories the student excels at */
  strongestAreas: SkillCategory[]
  /** Specific words/patterns that are frequently missed */
  frequentErrors: { item: string; category: SkillCategory; count: number; lastSeen: number }[]
}

export function createEmptyWeaknessProfile(): WeaknessProfile {
  const categories: SkillCategory[] = ['vocab', 'spelling', 'sentence', 'listening', 'speaking', 'phonics', 'grammar']
  const skills = {} as Record<SkillCategory, SkillStat>
  for (const cat of categories) {
    skills[cat] = {
      category: cat,
      attempts: 0,
      correct: 0,
      recentAccuracy: 0,
      trend: 'stable',
      avgResponseTime: 0,
      lastUpdated: Date.now(),
    }
  }
  return {
    skills,
    weakestAreas: [],
    strongestAreas: [],
    frequentErrors: [],
  }
}

export function updateSkillStat(
  profile: WeaknessProfile,
  category: SkillCategory,
  correct: boolean,
  responseTimeMs?: number
): WeaknessProfile {
  const stat = profile.skills[category]
  const newAttempts = stat.attempts + 1
  const newCorrect = stat.correct + (correct ? 1 : 0)

  // Track recent accuracy with a sliding window
  const recentWindow = 10
  const recentAccuracy = newAttempts <= recentWindow
    ? newCorrect / newAttempts
    : Math.min(1, (stat.recentAccuracy * (recentWindow - 1) + (correct ? 1 : 0)) / recentWindow)

  // Determine trend by comparing recent accuracy to overall
  const overallAccuracy = newCorrect / newAttempts
  let trend: SkillStat['trend'] = 'stable'
  if (newAttempts >= 5) {
    if (recentAccuracy > overallAccuracy + 0.1) trend = 'improving'
    else if (recentAccuracy < overallAccuracy - 0.1) trend = 'declining'
  }

  // Update average response time
  const avgResponseTime = responseTimeMs
    ? (stat.avgResponseTime * stat.attempts + responseTimeMs) / newAttempts
    : stat.avgResponseTime

  const newSkills = {
    ...profile.skills,
    [category]: {
      category,
      attempts: newAttempts,
      correct: newCorrect,
      recentAccuracy,
      trend,
      avgResponseTime,
      lastUpdated: Date.now(),
    },
  }

  // Recalculate weakest/strongest areas (only for skills with enough data)
  const qualified = Object.values(newSkills).filter(s => s.attempts >= 3)
  const sorted = [...qualified].sort((a, b) => a.recentAccuracy - b.recentAccuracy)
  const weakestAreas = sorted.slice(0, 2).map(s => s.category)
  const strongestAreas = [...sorted].reverse().slice(0, 2).map(s => s.category)

  return {
    skills: newSkills,
    weakestAreas,
    strongestAreas,
    frequentErrors: profile.frequentErrors,
  }
}

export function recordFrequentError(
  profile: WeaknessProfile,
  item: string,
  category: SkillCategory
): WeaknessProfile {
  const existing = profile.frequentErrors.find(e => e.item === item && e.category === category)
  let frequentErrors
  if (existing) {
    frequentErrors = profile.frequentErrors.map(e =>
      e.item === item && e.category === category
        ? { ...e, count: e.count + 1, lastSeen: Date.now() }
        : e
    )
  } else {
    frequentErrors = [...profile.frequentErrors, { item, category, count: 1, lastSeen: Date.now() }]
  }
  // Keep only top 20 most frequent errors
  frequentErrors.sort((a, b) => b.count - a.count)
  frequentErrors = frequentErrors.slice(0, 20)
  return { ...profile, frequentErrors }
}

export function getAccuracyByCategory(profile: WeaknessProfile, category: SkillCategory): number {
  const stat = profile.skills[category]
  if (stat.attempts === 0) return 0
  return stat.recentAccuracy
}

// ========================
// Adaptive Difficulty Engine
// ========================

export interface DifficultyState {
  /** Current difficulty multiplier (0.5–1.5, 1.0 = baseline) */
  multiplier: number
  /** Recent performance window (last N answers) */
  recentResults: boolean[]
  /** Total adjustments made */
  adjustments: number
  /** Last adjustment direction */
  lastDirection: 'up' | 'down' | 'none'
  /** Streak of correct answers at current difficulty */
  correctStreak: number
  /** Streak of wrong answers at current difficulty */
  wrongStreak: number
}

export function createDifficultyState(): DifficultyState {
  return {
    multiplier: 1.0,
    recentResults: [],
    adjustments: 0,
    lastDirection: 'none',
    correctStreak: 0,
    wrongStreak: 0,
  }
}

/**
 * Adaptive difficulty algorithm:
 * - 3 consecutive correct → increase difficulty
 * - 2 consecutive wrong → decrease difficulty
 * - Smooth transitions, kid-friendly bounds
 */
export function updateDifficulty(state: DifficultyState, correct: boolean): DifficultyState {
  const recentResults = [...state.recentResults, correct].slice(-15)

  let { multiplier, correctStreak, wrongStreak, adjustments, lastDirection } = state

  if (correct) {
    correctStreak += 1
    wrongStreak = 0
    if (correctStreak >= 3 && multiplier < 1.5) {
      multiplier = Math.min(1.5, multiplier + 0.1)
      adjustments += 1
      lastDirection = 'up'
      correctStreak = 0
    }
  } else {
    wrongStreak += 1
    correctStreak = 0
    if (wrongStreak >= 2 && multiplier > 0.5) {
      multiplier = Math.max(0.5, multiplier - 0.1)
      adjustments += 1
      lastDirection = 'down'
      wrongStreak = 0
    }
  }

  return { multiplier, recentResults, adjustments, lastDirection, correctStreak, wrongStreak }
}

/**
 * Determine if a layer change is warranted based on sustained performance.
 * Returns null if no change needed.
 */
export function shouldChangeLayer(
  difficulty: DifficultyState,
  currentLayer: LearningLayer
): { newLayer: LearningLayer; reason: string } | null {
  const recentCorrect = difficulty.recentResults.slice(-10)
  const accuracy = recentCorrect.length > 0
    ? recentCorrect.filter(Boolean).length / recentCorrect.length
    : 0

  // Upgrade: consistently high performance at high difficulty
  if (accuracy >= 0.85 && difficulty.multiplier >= 1.3 && recentCorrect.length >= 8) {
    if (currentLayer === 'C') return { newLayer: 'B', reason: '你的表现非常出色！系统检测到你可以挑战更高难度的内容了！' }
    if (currentLayer === 'B') return { newLayer: 'A', reason: '太厉害了！你已经准备好进入进阶模式了！' }
  }

  // Downgrade: struggling at current difficulty
  if (accuracy <= 0.4 && difficulty.multiplier <= 0.7 && recentCorrect.length >= 6) {
    if (currentLayer === 'A') return { newLayer: 'B', reason: '别担心！系统为你调整到更舒适的学习节奏，我们一起慢慢进步！' }
    if (currentLayer === 'B') return { newLayer: 'C', reason: '系统为你开启了辅助模式，会有更多提示帮助你学习！' }
  }

  return null
}

// ========================
// Learning Style Adaptation
// ========================

export interface StyleAdaptation {
  style: LearningStyleType
  /** Preferred scene types */
  preferredScenes: string[]
  /** Agent emphasis */
  agentFocus: AgentRole
  /** Dialogue style adjustment */
  dialogueTone: 'encouraging' | 'competitive' | 'narrative' | 'analytical'
  /** Reward frequency */
  rewardFrequency: 'high' | 'medium' | 'low'
  /** Interaction pace */
  pace: 'fast' | 'medium' | 'slow'
}

type AgentRole = 'teacher' | 'ta' | 'classmate-1' | 'classmate-2' | 'system'

export function getStyleAdaptation(style: LearningStyleType | null): StyleAdaptation {
  switch (style) {
    case 'imagination':
      return {
        style: 'imagination',
        preferredScenes: ['story', 'narrative', 'dialogue', 'word-reveal'],
        agentFocus: 'teacher',
        dialogueTone: 'narrative',
        rewardFrequency: 'medium',
        pace: 'medium',
      }
    case 'challenge':
      return {
        style: 'challenge',
        preferredScenes: ['boss-battle', 'quiz', 'spelling', 'bonus-round'],
        agentFocus: 'classmate-2',
        dialogueTone: 'competitive',
        rewardFrequency: 'low',
        pace: 'fast',
      }
    case 'visual':
      return {
        style: 'visual',
        preferredScenes: ['word-match', 'whiteboard', 'listening', 'word-reveal'],
        agentFocus: 'classmate-1',
        dialogueTone: 'encouraging',
        rewardFrequency: 'high',
        pace: 'medium',
      }
    case 'expression':
      return {
        style: 'expression',
        preferredScenes: ['speaking', 'dialogue', 'conversation', 'sentence-builder'],
        agentFocus: 'teacher',
        dialogueTone: 'encouraging',
        rewardFrequency: 'medium',
        pace: 'medium',
      }
    default:
      return {
        style: 'visual',
        preferredScenes: ['word-reveal', 'word-match', 'sentence-builder', 'quiz'],
        agentFocus: 'teacher',
        dialogueTone: 'encouraging',
        rewardFrequency: 'medium',
        pace: 'medium',
      }
  }
}

// ========================
// Smart Review Recommendations
// ========================

export interface ReviewRecommendation {
  type: 'word-review' | 'weakness-practice' | 'spaced-repetition' | 'mastery-check' | 'error-drill'
  title: string
  description: string
  items: string[]
  priority: number // 1 (highest) to 5 (lowest)
  emoji: string
  estimatedXp: number
}

export function generateRecommendations(
  srsCards: Map<string, SRSCard>,
  weaknessProfile: WeaknessProfile,
  _recentErrors: { item: string; category: SkillCategory }[]
): ReviewRecommendation[] {
  const recommendations: ReviewRecommendation[] = []

  // 1. Spaced repetition — words due for review
  const dueCards = Array.from(srsCards.values()).filter(isDueForReview)
  if (dueCards.length > 0) {
    recommendations.push({
      type: 'spaced-repetition',
      title: '记忆巩固',
      description: `${dueCards.length}个单词到了复习时间，趁记忆还在快来巩固！`,
      items: dueCards.map(c => c.word),
      priority: 1,
      emoji: '🔄',
      estimatedXp: dueCards.length * 10,
    })
  }

  // 2. Weakness practice — target lowest accuracy category
  if (weaknessProfile.weakestAreas.length > 0) {
    const weakest = weaknessProfile.weakestAreas[0]
    const stat = weaknessProfile.skills[weakest]
    if (stat.attempts >= 3) {
      const categoryNames: Record<SkillCategory, string> = {
        vocab: '词汇',
        spelling: '拼写',
        sentence: '句型',
        listening: '听力',
        speaking: '口语',
        phonics: '自然拼读',
        grammar: '语法',
      }
      recommendations.push({
        type: 'weakness-practice',
        title: '弱点突破',
        description: `你的${categoryNames[weakest]}正确率是${Math.round(stat.recentAccuracy * 100)}%，专属训练来帮你提升！`,
        items: [],
        priority: 2,
        emoji: '🎯',
        estimatedXp: 50,
      })
    }
  }

  // 3. Error drill — frequently missed items
  if (weaknessProfile.frequentErrors.length > 0) {
    const topErrors = weaknessProfile.frequentErrors.slice(0, 5)
    recommendations.push({
      type: 'error-drill',
      title: '错题攻克',
      description: `这些是你最容易出错的内容，攻克它们就能大幅提升！`,
      items: topErrors.map(e => e.item),
      priority: 3,
      emoji: '⚔️',
      estimatedXp: topErrors.length * 15,
    })
  }

  // 4. Mastery check — words close to mastering
  const nearMastery = Array.from(srsCards.values())
    .filter(c => c.mastery >= 60 && c.mastery < 85)
    .sort((a, b) => b.mastery - a.mastery)
  if (nearMastery.length > 0) {
    recommendations.push({
      type: 'mastery-check',
      title: '精通挑战',
      description: `${nearMastery.length}个单词即将精通，再答对几次就能加冕！`,
      items: nearMastery.map(c => c.word).slice(0, 5),
      priority: 4,
      emoji: '👑',
      estimatedXp: nearMastery.length * 20,
    })
  }

  // 5. Word review — low mastery words
  const lowMastery = Array.from(srsCards.values())
    .filter(c => c.mastery < 30 && c.repetitions > 0)
    .sort((a, b) => a.mastery - b.mastery)
  if (lowMastery.length > 0) {
    recommendations.push({
      type: 'word-review',
      title: '基础巩固',
      description: '这些单词还需要多练习，从基础开始打好地基！',
      items: lowMastery.map(c => c.word).slice(0, 5),
      priority: 5,
      emoji: '🌱',
      estimatedXp: lowMastery.length * 8,
    })
  }

  return recommendations.sort((a, b) => a.priority - b.priority)
}

// ========================
// Metacognitive Insights
// ========================

export interface MetacognitiveInsight {
  type: 'self-awareness' | 'strategy' | 'progress' | 'goal'
  title: string
  message: string
  emoji: string
  actionable: boolean
}

export function generateInsights(
  weaknessProfile: WeaknessProfile,
  srsCards: Map<string, SRSCard>,
  sessionStats: { correctCount: number; totalCount: number; maxCombo: number }
): MetacognitiveInsight[] {
  const insights: MetacognitiveInsight[] = []
  const accuracy = sessionStats.totalCount > 0
    ? sessionStats.correctCount / sessionStats.totalCount
    : 0

  // Self-awareness: identify learning patterns
  if (weaknessProfile.weakestAreas.length > 0) {
    const weakest = weaknessProfile.weakestAreas[0]
    const stat = weaknessProfile.skills[weakest]
    if (stat.attempts >= 5) {
      const categoryNames: Record<SkillCategory, string> = {
        vocab: '词汇记忆', spelling: '拼写', sentence: '句型构造',
        listening: '听力理解', speaking: '口语表达', phonics: '自然拼读', grammar: '语法'
      }
      insights.push({
        type: 'self-awareness',
        title: '自我认知',
        message: `你在${categoryNames[weakest]}方面还需要加强。正确率${Math.round(stat.recentAccuracy * 100)}%。试试用不同的方法来学习——比如把单词编成小故事，或者多听几遍录音！`,
        emoji: '🧠',
        actionable: true,
      })
    }
  }

  // Strategy suggestion
  if (accuracy < 0.6 && sessionStats.totalCount >= 5) {
    insights.push({
      type: 'strategy',
      title: '学习策略',
      message: '遇到困难时，不要急着回答。先大声读出来，想想单词的意思，再选择答案。慢一点没关系，准确更重要！',
      emoji: '💡',
      actionable: true,
    })
  }

  // Progress celebration
  if (sessionStats.maxCombo >= 5) {
    insights.push({
      type: 'progress',
      title: '进步看得见',
      message: `你刚才达成了${sessionStats.maxCombo}连击！这说明你正在进入"心流"状态——学习效率最高的时候。继续保持！`,
      emoji: '🔥',
      actionable: false,
    })
  }

  // Goal setting
  const masteredCount = Array.from(srsCards.values()).filter(c => c.mastery >= 85).length
  const learningCount = Array.from(srsCards.values()).filter(c => c.mastery >= 30 && c.mastery < 85).length
  if (masteredCount > 0 || learningCount > 0) {
    insights.push({
      type: 'goal',
      title: '今日目标',
      message: `你已经精通${masteredCount}个单词，还有${learningCount}个正在学习中。把正在学习的单词全部精通，就是今天的挑战！`,
      emoji: '🎯',
      actionable: true,
    })
  }

  // Trend insight
  const improvingSkills = Object.values(weaknessProfile.skills).filter(s => s.trend === 'improving')
  if (improvingSkills.length > 0) {
    insights.push({
      type: 'progress',
      title: '上升趋势',
      message: `你的${improvingSkills.map(s => s.category).join('、')}最近在进步！坚持下去，你正在变得更强！`,
      emoji: '📈',
      actionable: false,
    })
  }

  return insights
}

// ========================
// Personalization State
// ========================

export interface PersonalizationState {
  srsCards: Record<string, SRSCard>
  weaknessProfile: WeaknessProfile
  difficulty: DifficultyState
  insights: MetacognitiveInsight[]
  recommendations: ReviewRecommendation[]
  totalReviews: number
  masterySnapshot: { mastered: number; learning: number; novice: number }
}

export function createInitialPersonalizationState(): PersonalizationState {
  return {
    srsCards: {},
    weaknessProfile: createEmptyWeaknessProfile(),
    difficulty: createDifficultyState(),
    insights: [],
    recommendations: [],
    totalReviews: 0,
    masterySnapshot: { mastered: 0, learning: 0, novice: 0 },
  }
}

export function updatePersonalizationOnAnswer(
  state: PersonalizationState,
  word: string,
  category: SkillCategory,
  correct: boolean,
  responseTimeMs?: number
): PersonalizationState {
  // Update SRS
  const existingCard = state.srsCards[word] || createSRSCard(word)
  const updatedCard = reviewSRSCard(existingCard, correct, responseTimeMs)
  const newSrsCards = { ...state.srsCards, [word]: updatedCard }

  // Update weakness profile
  let newProfile = updateSkillStat(state.weaknessProfile, category, correct, responseTimeMs)
  if (!correct) {
    newProfile = recordFrequentError(newProfile, word, category)
  }

  // Update difficulty
  const newDifficulty = updateDifficulty(state.difficulty, correct)

  // Recalculate mastery snapshot
  const cards = Object.values(newSrsCards)
  const masterySnapshot = {
    mastered: cards.filter(c => c.mastery >= 85).length,
    learning: cards.filter(c => c.mastery >= 30 && c.mastery < 85).length,
    novice: cards.filter(c => c.mastery < 30).length,
  }

  // Generate fresh recommendations
  const srsMap = new Map(Object.entries(newSrsCards))
  const recommendations = generateRecommendations(srsMap, newProfile, [])

  return {
    srsCards: newSrsCards,
    weaknessProfile: newProfile,
    difficulty: newDifficulty,
    insights: state.insights, // Insights generated separately to avoid overhead
    recommendations,
    totalReviews: state.totalReviews + 1,
    masterySnapshot,
  }
}

export function refreshInsights(
  state: PersonalizationState,
  sessionStats: { correctCount: number; totalCount: number; maxCombo: number }
): PersonalizationState {
  const insights = generateInsights(state.weaknessProfile, new Map(Object.entries(state.srsCards)), sessionStats)
  return { ...state, insights }
}
